import { useState } from "react";
import { AlertTriangle, ChevronDown } from "lucide-react";
import { Badge } from "@/components/atoms/studio/ui/badge";
import { EngineEffectsBrowser } from "./EngineEffectsBrowser";
import {
  carriedFractionLabel,
  countLabel,
  coverageCaveats,
  coverageVerdict,
  suppressedTone,
  toneBadgeVariant,
} from "@/lib/migrations/coverage";
import { engineTaskPath } from "@/services/migrations/engine";
import type { EngineCoverageBucket, EngineTaskReport } from "@/services/migrations/engine";

/**
 * The six coverage buckets and the carried fraction, for one engine task.
 *
 * Three rules are structural here, not stylistic:
 *
 *  1. `undeclared` RENDERS AT ZERO. The engine reports it at zero deliberately — "we
 *     checked, and nothing was lost silently" is a claim worth making explicitly, and a
 *     bucket that only appears when it is non-empty teaches readers to stop looking for
 *     it. It is never omitted.
 *
 *  2. `undeclared > 0` SUPPRESSES SUCCESS. Silent loss must be incapable of looking
 *     clean, so the whole panel's green is stripped (see `suppressedTone`) and a
 *     destructive banner leads. A 100%-looking carried fraction sitting above two
 *     effects that vanished without explanation is the single most dangerous thing this
 *     screen could show.
 *
 *  3. EVERY BUCKET OPENS, INCLUDING `carried`. The counts come from the report and the
 *     lists come from `/effects`, paged — so no number on this screen has to be taken on
 *     faith. `carried` in particular is unreachable any other way: the report strips it.
 */
export function CoveragePanel({
  planId,
  task,
  /** True when ANY task in the plan lost something silently: success is suppressed
   *  plan-wide, not just on the offending task. */
  successSuppressed,
}: {
  planId: string;
  task: EngineTaskReport;
  successSuppressed: boolean;
}) {
  const verdict = coverageVerdict(task);
  // A task can be clean while its sibling is not; the plan-wide flag wins.
  const suppressed = successSuppressed || verdict.successSuppressed;
  const caveats = coverageCaveats(task);
  const taskPath = engineTaskPath(task);
  const [open, setOpen] = useState<EngineCoverageBucket | null>(null);

  return (
    <div className="space-y-4">
      {verdict.silentLoss ? (
        <SilentLossBanner count={verdict.undeclared} known={verdict.undeclaredKnown} />
      ) : null}

      {/* ── the measured claim ─────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span
          className={[
            "font-mono text-2xl tabular-nums",
            suppressed ? "text-[hsl(var(--accent-warm))]" : "text-success",
          ].join(" ")}
        >
          {carriedFractionLabel(verdict.carriedFraction, verdict.total)}
        </span>
        <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
          carried · {verdict.carried} of {verdict.total} effects
        </span>
      </div>

      {caveats.length > 0 ? (
        <ul className="space-y-1">
          {caveats.map((caveat) => (
            <li key={caveat} className="text-xs text-[hsl(var(--accent-warm))]">
              {caveat}
            </li>
          ))}
        </ul>
      ) : null}

      {/* ── every effect is in exactly one of these six ────────────────────── */}
      <ul className="divide-y divide-border">
        {verdict.buckets.map((bucket) => {
          const isOpen = open === bucket.bucket;
          return (
            <li key={bucket.bucket} className="py-2">
              <button
                type="button"
                className="flex w-full flex-wrap items-start justify-between gap-3 text-left"
                onClick={() => setOpen(isOpen ? null : bucket.bucket)}
                aria-expanded={isOpen}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 font-mono text-sm text-foreground">
                    <ChevronDown
                      className={[
                        "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200",
                        isOpen ? "rotate-0" : "-rotate-90",
                      ].join(" ")}
                    />
                    {bucket.label}
                  </div>
                  <p className="mt-0.5 max-w-prose pl-5 text-xs text-muted-foreground">
                    {bucket.detail}
                  </p>
                </div>
                <Badge variant={toneBadgeVariant(suppressedTone(bucket.tone, suppressed))}>
                  {countLabel(bucket.count, bucket.known)}
                </Badge>
              </button>
              {isOpen ? (
                <div className="mt-2 pl-5">
                  <EngineEffectsBrowser
                    planId={planId}
                    taskPath={taskPath}
                    bucket={bucket.bucket}
                    expectedTotal={bucket.known ? bucket.count : undefined}
                  />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * The one thing on this screen that outranks the coverage number. It leads, it is
 * destructive, and it names the count rather than hinting at it — or says plainly that
 * the count never arrived, which is not the same as zero and must not read like it.
 */
function SilentLossBanner({ count, known }: { count: number; known: boolean }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1 leading-relaxed">
        {known ? (
          <span className="font-semibold">
            {count} effect{count === 1 ? "" : "s"} dropped with no declared reason.
          </span>
        ) : (
          <span className="font-semibold">
            The engine did not report how many effects were dropped without a reason.
          </span>
        )}{" "}
        {known
          ? "The engine could not say what was lost or how to recover it, so nothing on this screen is presented as a success."
          : "An absent count is not a zero, so nothing on this screen is presented as a success."}{" "}
        Treat the coverage fraction above as an upper bound, not a result.
      </span>
    </div>
  );
}
